import { createContext, useContext } from 'react';
import type { IdTokenClaims, UserInfoResponse, SignInOptions } from '@logto/browser';

/**
 * Sign-in options shared by both web and native adapters.
 * Re-uses @logto/browser SignInOptions for full type compatibility with the SDK.
 */
export type LogtoSignInOptions = SignInOptions;

/**
 * Unified adapter interface consumed by all auth hooks and components.
 *
 * AIDEV-NOTE: Both WebLogtoAdapterProvider and NativeLogtoAdapterProvider
 * expose this exact shape. Consumers use useLogtoAdapter() and never
 * import from @logto/react or @logto/capacitor directly.
 */
export interface LogtoAdapterValue {
  isAuthenticated: boolean;
  isLoading: boolean;
  error: Error | undefined;
  signIn: (options: LogtoSignInOptions) => Promise<void>;
  signOut: (postLogoutRedirectUri?: string) => Promise<void>;
  getAccessToken: (resource?: string, organizationId?: string) => Promise<string>;
  getIdTokenClaims: () => Promise<IdTokenClaims | undefined>;
  fetchUserInfo: () => Promise<UserInfoResponse>;
  clearAllTokens: () => Promise<void>;
}

export const LogtoAdapterContext = createContext<LogtoAdapterValue | null>(null);

/**
 * Hook to consume the Logto adapter context.
 * Throws if used outside a WebLogtoAdapterProvider or NativeLogtoAdapterProvider.
 */
export function useLogtoAdapter(): LogtoAdapterValue {
  const ctx = useContext(LogtoAdapterContext);
  if (!ctx) {
    throw new Error('useLogtoAdapter() must be used within a LogtoAdapterProvider');
  }
  return ctx;
}
